import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Order } from '../common/order';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Page } from '../common/page';
import { OrderSummary } from '../common/order-summary';

// Interfaz para la solicitud de creación de orden
export interface CreateOrderRequest {
  items: { productId: number; formatKey: string; quantity: number }[];
  paymentMethod: 'PAYPAL' | 'CASH_ON_DELIVERY';
  shippingAddress?: string;
  cellphone?: string;
}

// Interfaz para la respuesta de creación de orden (inicio de pago)
export interface CreateOrderResponse {
  orderId: string;
  approvalUrl: string;
}

// Interfaz para la solicitud de captura de pago
export interface CaptureOrderRequest {
  paymentToken: string; // El token de la pasarela, ej. paymentId de PayPal
  payerId?: string;     // El PayerID (específico de PayPal)
}


@Injectable({
  providedIn: 'root'
})
export class OrderService {
  private apiUrl: string = `${environment.apiUrl}/orders`;
  private adminApiUrl: string = `${environment.apiUrl}/admin/orders`;

  constructor(private httpClient: HttpClient) { }

  /**
   * Llama al backend para crear una orden y obtener la URL de pago.
   * @param orderRequest Los datos de la orden.
   */
  createOrder(orderRequest: CreateOrderRequest): Observable<CreateOrderResponse> {
    return this.httpClient.post<CreateOrderResponse>(this.apiUrl, orderRequest);
  }

  /**
   * Llama al backend para capturar y confirmar el pago de una orden.
   * @param orderId El ID de la orden interna.
   * @param captureRequest Los datos recibidos de la pasarela de pago.
   */
  captureOrder(orderId: string, captureRequest: CaptureOrderRequest): Observable<any> {
    const url = `${this.apiUrl}/${orderId}/capture`;
    return this.httpClient.post(url, captureRequest);
  }

  updateOrder(orderId: number, newState: string): Observable<any> {
    const url = `${this.apiUrl}/${orderId}/state`;
    const body = { state: newState };
    return this.httpClient.patch(url, body);
  }

  getOrderByUser(page: number, size: number): Observable<Page<Order>> {
    let params = new HttpParams();
    params = params.append('page', page.toString());
    params = params.append('size', size.toString());
    return this.httpClient.get<Page<Order>>(`${this.apiUrl}/my-history`, { params: params });
  }

  getOrderById(orderId: number): Observable<Order> {
    return this.httpClient.get<Order>(`${this.apiUrl}/${orderId}`);
  }

  // Admin methods
  getOrders(page: number, size: number, status?: string, customerQuery?: string): Observable<Page<OrderSummary>> {
    let params = new HttpParams();
    params = params.append('page', page.toString());
    params = params.append('size', size.toString());
    if (status) {
      params = params.append('status', status);
    }
    if (customerQuery) {
      params = params.append('customerQuery', customerQuery);
    }
    return this.httpClient.get<Page<OrderSummary>>(this.adminApiUrl, { params: params });
  }

  getAdminOrderById(orderId: number): Observable<Order> {
    return this.httpClient.get<Order>(`${this.adminApiUrl}/${orderId}`);
  }

  updateOrderStatus(orderId: number, newState: string): Observable<any> {
    const url = `${this.adminApiUrl}/${orderId}/state`;
    const body = { newState: newState };
    return this.httpClient.patch(url, body);
  }
}